import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Eye, Play } from 'lucide-react';
import BasicInfoForm from '../BasicInfoForm';
import VariantsConfig from '../VariantsConfig';
import SettingsConfig from '../SettingsConfig';
import TestSummary from '../TestSummary';

const TestConfigWrapper = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const testType = searchParams.get('type') || 'element';

    const [currentStep, setCurrentStep] = useState(1);

    // Refactored formData structure
    const [formData, setFormData] = useState({
        // Step 1: Basic Info
        basicInfo: null,

        // Step 2: Variants
        variants: null,

        // Step 3: Settings
        settings: null,

        // Step 4: Summary (read-only, no data to store)
        // summary: null
    });

    const testTypeLabels = {
        element: 'Element Test',
    };

    const steps = [
        { id: 1, title: 'Basic Info', completed: false },
        { id: 2, title: 'Variants', completed: false },
        { id: 3, title: 'Settings', completed: false },
        { id: 4, title: 'Summary', completed: false }
    ];

    // Update form data - merges new data with existing
    const handleFormDataUpdate = (data) => {
        setFormData(prev => ({ ...prev, ...data }));
    };

    const handleNext = () => {
        if (currentStep < 4) {
            setCurrentStep(currentStep + 1);
        }
    };

    const handleBack = () => {
        if (currentStep > 1) {
            setCurrentStep(currentStep - 1);
        } else {
            navigate('/element-ab-test');
        }
    };

    // Debug log
    console.log("Current Step:", currentStep);
    console.log("Form Data:", formData);

    const renderStepContent = () => {
        switch (currentStep) {
            case 1:
                return (
                    <BasicInfoForm
                        testType={testType}
                        formData={formData}
                        onUpdate={handleFormDataUpdate}
                        onNext={handleNext}
                    />
                );
            case 2:
                return (
                    <VariantsConfig
                        testType={testType}
                        formData={formData}
                        onUpdate={handleFormDataUpdate}
                        onNext={handleNext}
                    />
                );
            case 3:
                return (
                    <SettingsConfig
                        testType={testType}
                        formData={formData}
                        onUpdate={handleFormDataUpdate}
                        onNext={handleNext}
                    />
                );
            case 4:
                return (
                    <TestSummary
                        testType={testType}
                        formData={formData}
                        onBack={() => setCurrentStep(3)}
                        onUpdate={handleFormDataUpdate}
                    />
                );
            default:
                return null;
        }
    };

    // Check if step is completed (has data)
    const isStepCompleted = (stepId) => {
        switch (stepId) {
            case 1:
                return formData.basicInfo !== null;
            case 2:
                return formData.variants !== null;
            case 3:
                return formData.settings !== null;
            case 4:
                // Step 4 (Summary) is only accessible when all previous steps are complete
                return formData.basicInfo !== null &&
                    formData.variants !== null &&
                    formData.settings !== null;
            default:
                return false;
        }
    };

    // Check if user can access a specific step
    const canAccessStep = (stepId) => {
        switch (stepId) {
            case 1:
                return true; // Always accessible
            case 2:
                return formData.basicInfo !== null; // Need step 1 complete
            case 3:
                return formData.basicInfo !== null && formData.variants !== null; // Need steps 1 & 2
            case 4:
                return formData.basicInfo !== null &&
                    formData.variants !== null &&
                    formData.settings !== null; // Need all steps
            default:
                return false;
        }
    };

    const handleStepClick = (stepId) => {
        // Only allow navigation to accessible steps
        if (canAccessStep(stepId)) {
            setCurrentStep(stepId);
        }
    };

    return (
        <div className="w-full px-6 py-8 min-h-screen max-md:px-4 max-md:py-5">
            {/* Header */}
            <div className="flex justify-between items-start mb-10 max-md:flex-col max-md:gap-5 max-md:items-stretch">
                <button onClick={handleBack} className="inline-flex items-center gap-2 px-4 py-2 bg-slate-50 border border-slate-200 rounded-lg text-slate-500 text-sm font-medium cursor-pointer transition-all duration-200 hover:bg-slate-100 hover:border-slate-300 hover:text-slate-600 flex-shrink-0">
                    <ArrowLeft size={20} />
                    Back
                </button>

                <div className="text-center flex-1 mx-10 max-md:mx-0 max-md:text-left">
                    <h1 className="text-3xl font-bold text-slate-800 mb-2 max-md:text-2xl">
                        Create {testTypeLabels[testType] || 'A/B Test'}
                    </h1>
                    <p className="text-base text-slate-500 m-0">
                        {currentStep === 1 && 'Set up the basic information for your A/B test'}
                        {currentStep === 2 && 'Configure your test variants'}
                        {currentStep === 3 && 'Set traffic distribution and targeting'}
                        {currentStep === 4 && 'Review and launch your test'}
                    </p>
                </div>

                {/* <div className="flex gap-3 items-center flex-shrink-0 max-md:justify-end">
                    <button className="flex items-center gap-2 px-4 py-2 bg-white border border-slate-200 rounded-lg text-slate-500 text-sm font-medium cursor-pointer transition-all duration-200 hover:border-slate-300 hover:text-slate-600">
                        <Eye size={16} />
                        Preview
                    </button>
                    {currentStep === 4 && (
                        <button className="flex items-center gap-2 px-4 py-2 bg-black border-none rounded-lg text-white text-sm font-medium cursor-pointer transition-all duration-200 hover:bg-gray-700">
                            <Play size={16} />
                            Start Test
                        </button>
                    )}
                </div> */}
            </div>

            {/* Progress Steps */}
            <div className="flex items-center justify-center mb-12 px-5 max-md:px-2.5">
                {steps.map((step, index) => (
                    <React.Fragment key={step.id}>
                        <div className={`flex flex-col items-center gap-2 relative ${
                            currentStep >= step.id ? 'step-active' : ''
                        } ${isStepCompleted(step.id) || currentStep > step.id ? 'step-completed' : ''}`}>
                            <div className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold text-sm transition-all duration-300 border-2 ${
                                currentStep >= step.id 
                                    ? 'bg-blue-500 text-white border-blue-500' 
                                    : isStepCompleted(step.id) || currentStep > step.id
                                        ? 'bg-emerald-500 text-white border-emerald-500'
                                        : 'bg-slate-100 text-slate-500 border-slate-200'
                            }`}>
                                {isStepCompleted(step.id) && currentStep > step.id ? '✓' : step.id}
                            </div>
                            <span className={`text-sm font-medium transition-colors duration-300 max-md:text-xs ${
                                currentStep === step.id 
                                    ? 'text-blue-500 font-semibold' 
                                    : isStepCompleted(step.id)
                                        ? 'text-emerald-500'
                                        : 'text-slate-500'
                            }`}>
                                {step.title}
                            </span>
                        </div>
                        {index < steps.length - 1 && (
                            <div className={`w-30 h-0.5 mx-5 transition-colors duration-300 max-md:w-15 max-md:mx-2.5 ${
                                currentStep > step.id ? 'bg-emerald-500' : 'bg-slate-200'
                            }`} />
                        )}
                    </React.Fragment>
                ))}
            </div>

            {/* Step Content */}
            <div className="mx-auto">
                {renderStepContent()}
            </div>
        </div>
    );
};

export default TestConfigWrapper;